/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { useEffect, useRef } from "react";
import Editor from "@monaco-editor/react";
import type { EditorProps } from "@monaco-editor/react";
import { installJinjaPromptLanguage } from "./installPromptLanguage";
import type { PromptCatalog } from "./promptCatalog";

type MonacoInstance = Parameters<NonNullable<EditorProps["beforeMount"]>>[0];

type PromptEditorProps = {
  value: string;
  onChange: (value: string) => void;
  catalog: PromptCatalog;
  height?: number | string;
  languageId?: string;
  path?: string;
  theme?: "light" | "vs-dark";
  readOnly?: boolean;
};

export function PromptEditor({
  value,
  onChange,
  catalog,
  height = 500,
  languageId = "jinja-prompt",
  path = "",
  theme = "vs-dark",
  readOnly = false,
}: PromptEditorProps) {
  const monacoRef = useRef<MonacoInstance | null>(null);

  const handleBeforeMount: NonNullable<EditorProps["beforeMount"]> = (monaco) => {
    monacoRef.current = monaco;
    installJinjaPromptLanguage(monaco, catalog, languageId);
  };

  useEffect(() => {
    if (!monacoRef.current) {
      return;
    }

    installJinjaPromptLanguage(monacoRef.current, catalog, languageId);
  }, [catalog, languageId]);

  return (
    <Editor
      height={height}
      language={languageId}
      path={path}
      value={value}
      beforeMount={handleBeforeMount}
      onChange={(nextValue) => onChange(nextValue ?? "")}
      theme={theme}
      options={{
        readOnly,
        automaticLayout: true,
        minimap: { enabled: false },
        wordWrap: "on",
        tabSize: 2,
        insertSpaces: true,
        scrollBeyondLastLine: false,
        suggestOnTriggerCharacters: true,
        quickSuggestions: {
          other: true,
          strings: true,
          comments: false,
        },
        snippetSuggestions: "top",
      }}
    />
  );
}
